/**
 * VTube Online — Live2D control-panel entry point
 *
 * Wires together:
 *  - Live2DViewer   (PixiJS + Cubism 4 rendering)
 *  - TrackingClient (WebSocket connection to the relay server)
 *  - Auth           (Discord OAuth session check, login/logout)
 *  - Room           (create/join/leave rooms, member list, bridge cmd)
 *  - DOM controls   (upload, model selector, background, WS URL)
 *
 * Mirrors main.js for VRM; replace VRMViewer with Live2DViewer and point
 * uploads to /upload-live2d and model list to /live2d-models.
 */

import { Live2DViewer }   from './live2d-viewer.js'
import { TrackingClient } from './tracking-client.js'
import './style.css'

// ── DOM refs ──────────────────────────────────────────────────────────────────

const canvas          = /** @type {HTMLCanvasElement}  */ (document.getElementById('live2d-canvas'))
const fileInput       = /** @type {HTMLInputElement}   */ (document.getElementById('live2d-file'))
const chooseFileBtn   = /** @type {HTMLButtonElement}  */ (document.getElementById('choose-file-btn'))
const uploadBtn       = /** @type {HTMLButtonElement}  */ (document.getElementById('upload-btn'))
const uploadStatus    = /** @type {HTMLDivElement}     */ (document.getElementById('upload-status'))
const modelSelect     = /** @type {HTMLSelectElement}  */ (document.getElementById('model-select'))
const deleteModelBtn  = /** @type {HTMLButtonElement}  */ (document.getElementById('delete-model-btn'))
const wsUrlInput      = /** @type {HTMLInputElement}   */ (document.getElementById('ws-url'))
const connectBtn      = /** @type {HTMLButtonElement}  */ (document.getElementById('connect-btn'))
const wsDot           = /** @type {HTMLSpanElement}    */ (document.getElementById('ws-dot'))
const wsLabel         = /** @type {HTMLSpanElement}    */ (document.getElementById('ws-label'))
const fpsCounter      = /** @type {HTMLDivElement}     */ (document.getElementById('fps-counter'))
const trackingCounter = /** @type {HTMLDivElement}     */ (document.getElementById('tracking-counter'))
const bgSelect        = /** @type {HTMLSelectElement}  */ (document.getElementById('bg-select'))
const smoothSlider    = /** @type {HTMLInputElement}   */ (document.getElementById('smooth-slider'))
const smoothValue     = /** @type {HTMLSpanElement}    */ (document.getElementById('smooth-value'))
const loadingOverlay  = /** @type {HTMLDivElement}     */ (document.getElementById('loading-overlay'))
const placeholder     = /** @type {HTMLDivElement}     */ (document.getElementById('placeholder'))
const bridgeCmd       = /** @type {HTMLElement}        */ (document.getElementById('bridge-cmd'))

// Auth DOM
const authLoggedOut = /** @type {HTMLDivElement}     */ (document.getElementById('auth-logged-out'))
const authLoggedIn  = /** @type {HTMLDivElement}     */ (document.getElementById('auth-logged-in'))
const authError     = /** @type {HTMLDivElement}     */ (document.getElementById('auth-error'))
const modelSection  = /** @type {HTMLElement}        */ (document.getElementById('model-section'))
const userAvatar    = /** @type {HTMLImageElement}   */ (document.getElementById('user-avatar'))
const userName      = /** @type {HTMLSpanElement}    */ (document.getElementById('user-name'))
const logoutBtn     = /** @type {HTMLButtonElement}  */ (document.getElementById('logout-btn'))

// Room DOM
const roomSection      = /** @type {HTMLElement}       */ (document.getElementById('room-section'))
const roomIdle         = /** @type {HTMLDivElement}    */ (document.getElementById('room-idle'))
const roomActive       = /** @type {HTMLDivElement}    */ (document.getElementById('room-active'))
const createRoomBtn    = /** @type {HTMLButtonElement} */ (document.getElementById('create-room-btn'))
const joinCodeInput    = /** @type {HTMLInputElement}  */ (document.getElementById('join-code-input'))
const joinRoomBtn      = /** @type {HTMLButtonElement} */ (document.getElementById('join-room-btn'))
const roomStatus       = /** @type {HTMLDivElement}    */ (document.getElementById('room-status'))
const roomCodeBadge    = /** @type {HTMLSpanElement}   */ (document.getElementById('room-code-badge'))
const copyCodeBtn      = /** @type {HTMLButtonElement} */ (document.getElementById('copy-code-btn'))
const leaveRoomBtn     = /** @type {HTMLButtonElement} */ (document.getElementById('leave-room-btn'))
const layoutBtns       = /** @type {NodeListOf<HTMLButtonElement>} */ (document.querySelectorAll('.layout-btn'))
const roomMemberList   = /** @type {HTMLUListElement}  */ (document.getElementById('room-member-list'))
const openViewerBtn    = /** @type {HTMLButtonElement} */ (document.getElementById('open-viewer-btn'))
const roomBridgeCmd    = /** @type {HTMLElement}       */ (document.getElementById('room-bridge-cmd'))
const copyBridgeCmdBtn = /** @type {HTMLButtonElement} */ (document.getElementById('copy-bridge-cmd-btn'))

// ── Base URL ──────────────────────────────────────────────────────────────────

const base     = import.meta.env.BASE_URL          // e.g. '/vtube-online/'
const basePath = base.replace(/\/$/, '')           // e.g. '/vtube-online'

// ── Download link hrefs ───────────────────────────────────────────────────────

;[['dl-bat', 'start-bridge.bat'], ['dl-sh', 'start-bridge.sh'],
  ['dl-bridge', 'bridge.js'],     ['dl-pkg', 'package.json']
].forEach(([id, file]) => {
  const el = document.getElementById(id)
  if (el) el.setAttribute('href', base + 'download/' + file)
})

// ── Live2D Viewer ─────────────────────────────────────────────────────────────

let viewer = null
try {
  viewer = new Live2DViewer(canvas)
  viewer.onFPS(fps => { fpsCounter.textContent = `FPS: ${fps}` })
} catch (err) {
  console.error('Live2DViewer init failed:', err)
  const errBox = document.getElementById('init-error')
  const errMsg = document.getElementById('init-error-msg')
  if (errBox) errBox.style.display = 'flex'
  if (errMsg) errMsg.textContent = err.message
}

// ── WebSocket defaults ────────────────────────────────────────────────────────

const wsProto   = location.protocol === 'https:' ? 'wss:' : 'ws:'
const defaultWs = location.port === '5173'
  ? `${wsProto}//${location.hostname}:3002`
  : `${wsProto}//${location.host}${base}`

wsUrlInput.value = defaultWs

const bridgeUrl = location.port === '5173'
  ? `ws://${location.hostname}:3002`
  : `wss://${location.host}${base}`
bridgeCmd.textContent = `node bridge.js --server ${bridgeUrl}`

// ── Module state ──────────────────────────────────────────────────────────────

/** @type {{userId:number,username:string,avatar:string}|null} */
let currentUser = null
let currentAvatarUrl = ''
/** @type {{ code: string, layout: string }|null} */
let currentRoom = null
let roomWs = null

// ── Auth ──────────────────────────────────────────────────────────────────────

async function fetchSession() {
  try {
    const res = await fetch(base + 'auth/me', { credentials: 'include' })
    if (res.status === 401) return null
    return await res.json()
  } catch { return null }
}

function applySession(user) {
  currentUser = user
  if (user) {
    authLoggedOut.style.display = 'none'
    authLoggedIn.style.display  = 'flex'
    userAvatar.src = user.avatar || ''
    userAvatar.alt = user.username
    userName.textContent = user.username
    modelSection.style.display = ''
    roomSection.style.display  = ''
  } else {
    authLoggedOut.style.display = ''
    authLoggedIn.style.display  = 'none'
    modelSection.style.display  = 'none'
    roomSection.style.display   = 'none'
    if (currentRoom) leaveRoom()
  }
}

// Show OAuth error from query params
const searchParams = new URLSearchParams(location.search)
if (searchParams.has('error')) {
  const msg = searchParams.get('error') === 'auth_failed'
    ? 'Sign-in failed — please try again.'
    : 'Something went wrong.'
  authError.textContent = msg
  authError.style.display = ''
  history.replaceState({}, '', location.pathname)
}

logoutBtn.addEventListener('click', async () => {
  await fetch(base + 'auth/logout', { method: 'POST', credentials: 'include' })
  applySession(null)
  modelSelect.innerHTML = '<option value="">— select a model —</option>'
  setStatus('', '')
})

// ── Startup ───────────────────────────────────────────────────────────────────

;(async () => {
  const user = await fetchSession()
  applySession(user)
  connectTracker(defaultWs)
  if (user) loadModelList()
})()

// ── Live2D upload ─────────────────────────────────────────────────────────────

chooseFileBtn.addEventListener('click', () => fileInput.click())

fileInput.addEventListener('change', () => {
  const name = fileInput.files?.[0]?.name
  if (name) chooseFileBtn.textContent = name
})

uploadBtn.addEventListener('click', async () => {
  if (!viewer) return setStatus('Live2D renderer failed to start — check console.', 'error')
  const file = fileInput.files?.[0]
  if (!file) return setStatus('Please choose a .zip archive first.', 'error')
  if (!file.name.toLowerCase().endsWith('.zip')) {
    return setStatus('File must be a .zip archive.', 'error')
  }

  setStatus('Uploading…', 'info')
  setLoading(true, 'Uploading…')

  const form = new FormData()
  form.append('live2d', file)

  try {
    const res = await fetch(base + 'upload-live2d', {
      method: 'POST',
      body: form,
      credentials: 'include',
    })
    if (res.status === 401) {
      applySession(null)
      return setStatus('Please sign in to upload models.', 'error')
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(body.error || res.statusText)
    }
    const { url } = await res.json()

    setStatus('Loading model…', 'info')
    await loadModelWithProgress(url)

    setCurrentAvatar(url)
    setStatus('✓ Model loaded', 'success')
    hidePlaceholder()
    await loadModelList()
  } catch (err) {
    setStatus(`Error: ${err.message}`, 'error')
  } finally {
    setLoading(false)
  }
})

// ── Model selector ────────────────────────────────────────────────────────────

modelSelect.addEventListener('change', async () => {
  const url = modelSelect.value
  deleteModelBtn.disabled = !url
  if (!url) return
  if (!viewer) return setStatus('Live2D renderer failed to start.', 'error')

  setStatus('Loading model…', 'info')
  try {
    await loadModelWithProgress(url)
    setCurrentAvatar(url)
    setStatus('✓ Model loaded', 'success')
    hidePlaceholder()
  } catch (err) {
    setStatus(`Error: ${err.message}`, 'error')
  } finally {
    setLoading(false)
  }
})

deleteModelBtn.addEventListener('click', async () => {
  const url = modelSelect.value
  if (!url) return

  // Extract folder from URL: /uploads/live2d/{folder}/...
  const parts  = url.split('/uploads/live2d/')[1]?.split('/') ?? []
  const folder = parts[0]
  if (!folder) return

  if (!confirm(`Remove "${modelSelect.options[modelSelect.selectedIndex].text}" from your account?`)) return

  try {
    const res = await fetch(base + `live2d-models/${encodeURIComponent(folder)}`,
      { method: 'DELETE', credentials: 'include' })
    if (!res.ok) throw new Error(await res.text())
    setStatus('Model removed.', 'info')
    await loadModelList()
  } catch (err) {
    setStatus(`Error: ${err.message}`, 'error')
  }
})

async function loadModelWithProgress(url) {
  if (!viewer) throw new Error('Live2D renderer not available')
  setLoading(true, 'Loading model… 0%')
  await viewer.loadModel(url, (pct, phase) => {
    setLoading(true, `${phase} model… ${pct}%`)
  })
}

async function loadModelList() {
  try {
    const res = await fetch(base + 'live2d-models', { credentials: 'include' })
    if (!res.ok) return
    const models = await res.json()
    modelSelect.innerHTML = '<option value="">— select a model —</option>'
    deleteModelBtn.disabled = true
    for (const m of models) {
      const opt = document.createElement('option')
      opt.value = m.url
      opt.textContent = m.original_name.replace(/\.zip$/i, '')
      modelSelect.appendChild(opt)
    }
  } catch { /* server might not be up yet */ }
}

function setCurrentAvatar(url) {
  currentAvatarUrl = url
  updateRoomBridgeCmd()
}

// ── WebSocket / tracking ──────────────────────────────────────────────────────

let tracker = null

connectBtn.addEventListener('click', () => {
  if (tracker?.connected) {
    tracker.disconnect()
    connectBtn.textContent = 'Connect'
  } else {
    connectTracker(wsUrlInput.value.trim() || defaultWs)
    connectBtn.textContent = 'Disconnect'
  }
})

wsUrlInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') connectBtn.click()
})

function connectTracker(url) {
  if (tracker) {
    tracker.removeEventListener('tracking',         onTracking)
    tracker.removeEventListener('connectionchange', onConnectionChange)
    tracker.disconnect()
  }
  tracker = new TrackingClient(url)
  tracker.addEventListener('tracking',         onTracking)
  tracker.addEventListener('connectionchange', onConnectionChange)
  tracker.connect()

  connectBtn.textContent = 'Disconnect'
  setDot('connecting')
  wsLabel.textContent = 'Connecting…'
}

let trackingPackets = 0
let firstPacketLogged = false

setInterval(() => {
  if (trackingPackets === 0) {
    trackingCounter.textContent = 'Tracking: no data'
    trackingCounter.style.color = 'var(--text-dim)'
  } else {
    trackingCounter.textContent = `Tracking: ${trackingPackets} fps ✓`
    trackingCounter.style.color = 'var(--green)'
  }
  trackingPackets = 0
}, 1000)

function onTracking(/** @type {Event & {data: object}} */ e) {
  trackingPackets++
  if (!firstPacketLogged) {
    firstPacketLogged = true
    console.log('[live2d] First tracking frame:', e.data)
  }
  viewer?.applyTracking(e.data)
}

function onConnectionChange(/** @type {Event & {connected: boolean}} */ e) {
  if (e.connected) {
    setDot('connected')
    wsLabel.textContent = 'Connected'
    connectBtn.textContent = 'Disconnect'
  } else {
    setDot('')
    wsLabel.textContent = 'Disconnected'
    connectBtn.textContent = 'Connect'
  }
}

// ── Background ────────────────────────────────────────────────────────────────

bgSelect.addEventListener('change', () => {
  viewer?.setBackground(bgSelect.value)
})

// ── Expression smoothing ──────────────────────────────────────────────────────

smoothSlider.addEventListener('input', () => {
  const alpha = parseFloat(smoothSlider.value)
  smoothValue.textContent = alpha.toFixed(2)
  viewer?.setBlendSmoothing(alpha)
})

// ── Room: create ──────────────────────────────────────────────────────────────

createRoomBtn.addEventListener('click', async () => {
  createRoomBtn.disabled = true
  try {
    const res = await fetch(base + 'rooms', { method: 'POST', credentials: 'include' })
    if (res.status === 401) {
      applySession(null)
      return setRoomStatus('Please sign in to create a room.', 'error')
    }
    if (!res.ok) throw new Error(await res.text())
    const { code } = await res.json()
    enterRoom(code)
  } catch (err) {
    setRoomStatus(`Error: ${err.message}`, 'error')
  } finally {
    createRoomBtn.disabled = false
  }
})

// ── Room: join ────────────────────────────────────────────────────────────────

joinRoomBtn.addEventListener('click', () => tryJoinRoom())
joinCodeInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') tryJoinRoom() })
joinCodeInput.addEventListener('input', () => {
  const pos = joinCodeInput.selectionStart
  joinCodeInput.value = joinCodeInput.value.toUpperCase()
  joinCodeInput.setSelectionRange(pos, pos)
})

async function tryJoinRoom() {
  const code = joinCodeInput.value.trim().toUpperCase()
  if (code.length !== 6) return setRoomStatus('Enter a 6-character room code.', 'error')

  joinRoomBtn.disabled = true
  try {
    const res = await fetch(base + `rooms/${code}`)
    if (res.status === 404) return setRoomStatus('Room not found — check the code and try again.', 'error')
    if (!res.ok) throw new Error(await res.text())
    const room = await res.json()
    if (room.streamerCount >= room.maxStreamers) {
      return setRoomStatus(`Room is full (${room.maxStreamers} streamers max).`, 'error')
    }
    enterRoom(code)
  } catch (err) {
    setRoomStatus(`Error: ${err.message}`, 'error')
  } finally {
    joinRoomBtn.disabled = false
  }
}

// ── Room: enter state ─────────────────────────────────────────────────────────

function enterRoom(code) {
  currentRoom = { code, layout: 'horizontal' }
  roomIdle.style.display   = 'none'
  roomActive.style.display = ''
  roomCodeBadge.textContent = code
  setRoomStatus('', '')
  layoutBtns.forEach(b => b.classList.toggle('active', b.dataset.layout === 'horizontal'))
  updateRoomBridgeCmd()
  connectRoomWs(code)
}

// ── Room: leave ───────────────────────────────────────────────────────────────

leaveRoomBtn.addEventListener('click', leaveRoom)

function leaveRoom() {
  if (roomWs) { roomWs.close(); roomWs = null }
  currentRoom = null
  roomIdle.style.display   = ''
  roomActive.style.display = 'none'
  roomMemberList.innerHTML = ''
  joinCodeInput.value = ''
  setRoomStatus('', '')
}

// ── Room: WebSocket ───────────────────────────────────────────────────────────

function connectRoomWs(code) {
  if (roomWs) { roomWs.close(); roomWs = null }
  const wsBase = location.port === '5173'
    ? `${wsProto}//${location.hostname}:3002`
    : `${wsProto}//${location.host}${basePath}`
  roomWs = new WebSocket(`${wsBase}?room=${code}`)

  roomWs.onopen = () => {
    roomWs.send(JSON.stringify({
      type:           'identify',
      role:           'viewer',
      selfStreamerId: currentUser ? String(currentUser.userId) : null,
    }))
  }
  roomWs.onmessage = ({ data }) => {
    try {
      const msg = JSON.parse(data)
      if (msg.type === 'room_state') renderMemberList(msg.streamers)
      if (msg.type === 'error')      setRoomStatus(msg.message, 'error')
    } catch {}
  }
  roomWs.onclose = () => {
    if (currentRoom?.code === code) {
      setTimeout(() => { if (currentRoom?.code === code) connectRoomWs(code) }, 3000)
    }
  }
  roomWs.onerror = () => {}
}

// ── Room: layout selector ─────────────────────────────────────────────────────

layoutBtns.forEach(btn => {
  btn.addEventListener('click', () => {
    layoutBtns.forEach(b => b.classList.remove('active'))
    btn.classList.add('active')
    if (currentRoom) { currentRoom.layout = btn.dataset.layout; updateRoomBridgeCmd() }
  })
})

// ── Room: copy code ───────────────────────────────────────────────────────────

copyCodeBtn.addEventListener('click', async () => {
  if (!currentRoom) return
  try {
    await navigator.clipboard.writeText(currentRoom.code)
    copyCodeBtn.textContent = '✓'
    setTimeout(() => { copyCodeBtn.textContent = '⧉' }, 1500)
  } catch {}
})

// ── Room: open viewer window ──────────────────────────────────────────────────

openViewerBtn.addEventListener('click', () => {
  if (!currentRoom) return
  const selfId = currentUser ? String(currentUser.userId) : ''
  const url = `${location.origin}${base}viewer.html` +
              `?room=${currentRoom.code}&self=${selfId}&layout=${currentRoom.layout}`
  window.open(url, '_blank', 'width=1920,height=1080,menubar=no,toolbar=no,location=no,status=no')
})

// ── Room: copy bridge command ─────────────────────────────────────────────────

copyBridgeCmdBtn.addEventListener('click', async () => {
  const cmd = roomBridgeCmd.textContent
  if (!cmd) return
  try {
    await navigator.clipboard.writeText(cmd)
    copyBridgeCmdBtn.textContent = '✓ Copied'
    setTimeout(() => { copyBridgeCmdBtn.textContent = 'Copy command' }, 1800)
  } catch {}
})

// ── Room: member list ─────────────────────────────────────────────────────────

function renderMemberList(streamers) {
  roomMemberList.innerHTML = ''
  if (!streamers || streamers.length === 0) {
    const li = document.createElement('li')
    li.className = 'room-member room-member-empty'
    li.textContent = 'No streamers connected yet'
    roomMemberList.appendChild(li)
    return
  }
  for (const s of streamers) {
    const li = document.createElement('li')
    li.className = 'room-member'
    const isSelf = currentUser && String(currentUser.userId) === s.streamerId
    li.innerHTML =
      `<span class="member-dot"></span>` +
      `<span class="member-name">${esc(s.username)}</span>` +
      (isSelf ? `<span class="member-self">(you)</span>` : '')
    roomMemberList.appendChild(li)
  }
}

// ── Room: bridge command generator ───────────────────────────────────────────

function updateRoomBridgeCmd() {
  if (!currentRoom || !currentUser) return
  const serverWs = location.port === '5173'
    ? `ws://${location.hostname}:3002`
    : `${wsProto}//${location.host}${basePath}`
  const avatarFull = currentAvatarUrl
    ? `${location.origin}${currentAvatarUrl}`
    : '(select a model above first)'
  const parts = [
    `node bridge.js`,
    `  --server    ${serverWs}`,
    `  --room      ${currentRoom.code}`,
    `  --user-id   ${currentUser.userId}`,
    `  --username  "${currentUser.username}"`,
    `  --avatar-url "${avatarFull}"`,
  ]
  roomBridgeCmd.textContent = parts.join(' \\\n')
}

function setRoomStatus(msg, type = '') {
  roomStatus.textContent = msg
  roomStatus.className   = `status ${type}`
}

// ── Bridge Setup: source selector tabs ───────────────────────────────────────

document.querySelectorAll('.source-tab').forEach(tab => {
  tab.addEventListener('click', () => {
    document.querySelectorAll('.source-tab').forEach(t => t.classList.remove('active'))
    tab.classList.add('active')
    const src = /** @type {string} */ (tab.dataset.source)
    document.querySelectorAll('.source-panel').forEach(panel => {
      /** @type {HTMLElement} */ (panel).style.display =
        panel.id === `src-${src}` ? '' : 'none'
    })
  })
})

// ── Helpers ───────────────────────────────────────────────────────────────────

function setStatus(msg, type = '') {
  uploadStatus.textContent = msg
  uploadStatus.className = `status ${type}`
}

function setLoading(visible, text = '') {
  loadingOverlay.classList.toggle('visible', visible)
  if (text) document.getElementById('loading-text').textContent = text
}

function hidePlaceholder() {
  placeholder.classList.add('hidden')
}

function setDot(state) {
  wsDot.className = `dot${state ? ' ' + state : ''}`
}

function esc(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
